import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["emailPanel", "emailsField", "errorMessage", "toggleButton", "submitButton"]

  toggleEmail(event) {
    event.preventDefault()
    if (!this.hasEmailPanelTarget) return

    const panel = this.emailPanelTarget
    panel.classList.toggle("hidden")
    this.clearError()

    const expanded = !panel.classList.contains("hidden")
    const button = event.currentTarget
    if (button) {
      button.setAttribute("aria-expanded", expanded.toString())
    }

    if (expanded) {
      panel.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  openShareWindow(event) {
    const button = event.currentTarget
    const url = button?.dataset.sharePanelUrl
    if (!url) return

    event.preventDefault()

    const width = parseInt(button.dataset.sharePanelWidth || "600", 10)
    const height = parseInt(button.dataset.sharePanelHeight || "600", 10)
    const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0
    const dualScreenTop = window.screenTop ?? window.screenY ?? 0
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || screen.width
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || screen.height

    const left = Math.max(0, dualScreenLeft + (viewportWidth - width) / 2)
    const top = Math.max(0, dualScreenTop + (viewportHeight - height) / 2)
    const features = `toolbar=0,status=0,menubar=0,scrollbars=1,resizable=1,width=${Math.round(width)},height=${Math.round(height)},top=${Math.round(top)},left=${Math.round(left)}`

    const shareWindow = window.open(url, "share", features)
    if (shareWindow) {
      shareWindow.opener = null
    } else {
      window.location.href = url
    }
  }

  closeEmailPanel(event) {
    if (event) event.preventDefault()
    if (!this.hasEmailPanelTarget) return
    this.emailPanelTarget.classList.add("hidden")
    this.clearError()
    if (this.hasToggleButtonTarget) {
      this.toggleButtonTargets.forEach((button) => {
        button.setAttribute("aria-expanded", "false")
      })
    }
  }

  submitEmailForm(event) {
    event.preventDefault()
    if (!this.hasEmailsFieldTarget) return
    this.clearError()

    const form = event.target
    const raw = this.emailsFieldTarget.value || ""
    const emails = raw.split(/[\s,;]+/).map((email) => email.trim()).filter(Boolean)

    if (emails.length === 0) {
      this.showError("Please add at least one valid email address.")
      return
    }

    const invalid = emails.filter((email) => !this.isValidEmail(email))
    if (invalid.length > 0) {
      this.showError(`These emails look incorrect: ${invalid.join(", ")}`)
      return
    }

    this.setSubmitting(true)

    const formData = new FormData(form)
    fetch(form.action, {
      method: form.method || "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken(),
        "X-Requested-With": "XMLHttpRequest"
      },
      body: formData
    })
      .then(async (response) => {
        let data = {}
        const contentType = response.headers.get("content-type") || ""
        if (contentType.includes("application/json")) {
          data = await response.json()
        }

        if (response.ok && data.success) {
          form.reset()
          this.closeEmailPanel()
          this.showFlash("notice", data.message || "Share email sent successfully.")
          return
        }

        const message = data.message || "We couldn’t send the share email. Please try again."
        this.showError(message)
      })
      .catch(() => {
        this.showError("Something went wrong while sending the invite. Please try again.")
      })
      .finally(() => {
        this.setSubmitting(false)
      })
  }

  isValidEmail(value) {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailPattern.test(value)
  }

  showError(message) {
    if (!this.hasErrorMessageTarget) return
    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.classList.remove("hidden")
  }

  clearError() {
    if (!this.hasErrorMessageTarget) return
    this.errorMessageTarget.textContent = ""
    this.errorMessageTarget.classList.add("hidden")
  }

  setSubmitting(disabled) {
    if (!this.hasSubmitButtonTarget) return

    const button = this.submitButtonTarget
    if (disabled) {
      button.dataset.originalText = button.textContent
      button.textContent = "Sending..."
      button.disabled = true
      button.classList.add("opacity-70", "cursor-not-allowed")
      button.setAttribute("aria-busy", "true")
    } else {
      if (button.dataset.originalText) {
        button.textContent = button.dataset.originalText
      }
      button.disabled = false
      button.classList.remove("opacity-70", "cursor-not-allowed")
      button.removeAttribute("aria-busy")
    }
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.getAttribute("content") : ""
  }

  showFlash(type, message) {
    if (!message) return

    const colorClasses = {
      notice: "bg-emerald-50 text-emerald-700 border-emerald-200",
      alert: "bg-red-50 text-red-700 border-red-200",
      error: "bg-red-50 text-red-700 border-red-200"
    }
    const styleClasses = colorClasses[type] || "bg-slate-100 text-slate-700 border-slate-200"

    const wrapper = document.createElement("div")
    wrapper.className = "px-4 pt-4 sm:px-6 lg:px-8 flash-message"

    const container = document.createElement("div")
    container.className = "mx-auto max-w-3xl"

    const alertBox = document.createElement("div")
    alertBox.className = `flex items-start justify-between gap-3 rounded-2xl border px-4 py-3 text-sm font-medium shadow-sm ${styleClasses}`

    const text = document.createElement("span")
    text.textContent = message

    const closeButton = document.createElement("button")
    closeButton.type = "button"
    closeButton.textContent = "Dismiss"
    closeButton.className = "text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-700"
    closeButton.setAttribute("aria-label", "Dismiss notification")
    closeButton.addEventListener("click", () => this.dismissFlash(wrapper))

    alertBox.appendChild(text)
    alertBox.appendChild(closeButton)
    container.appendChild(alertBox)
    wrapper.appendChild(container)
    document.body.prepend(wrapper)

    wrapper.classList.add("opacity-0", "-translate-y-2")
    requestAnimationFrame(() => {
      wrapper.classList.add("transition", "duration-200")
      wrapper.classList.remove("opacity-0", "-translate-y-2")
    })

    setTimeout(() => this.dismissFlash(wrapper), 8000)
  }

  dismissFlash(element) {
    if (!element || element.dataset.dismissed) return

    element.dataset.dismissed = "true"
    element.classList.add("opacity-0", "translate-y-2", "transition", "duration-200")
    setTimeout(() => {
      element.remove()
    }, 200)
  }
}
